import { useMemo } from 'react'
import { useReadContracts } from 'wagmi'
import { ADDRESSES, IS_LIVE, ZERO_ADDR } from '@/lib/contracts'
import gaugeAbi from '@/lib/abis/GaugeController.json'
import farmAbi from '@/lib/abis/HorizonEmissions.json'
import veAbi from '@/lib/abis/HorizonVoteEscrow.json'
import { POLL_SLOW } from '@/lib/utils'

const WEEK = 7 * 86_400
const notDeployed = (a) => !IS_LIVE || (a || '').toLowerCase() === ZERO_ADDR

/// The ve(3,3) gauge vote (GaugeController): each veNFT holder allocates their voting power across
/// emission GAUGES for the current WEEKLY epoch, and the tally drives HorizonEmissions' per-farm
/// weights. Votes reset each epoch, so a holder re-casts weekly.
///
/// A gauge is a HorizonEmissions vault farm addressed by INDEX. This reads the farm table
/// (`vaultFarmCount` + `getVaultFarm`) alongside the gauge's own per-farm state - `farmMode`
/// (VOTED vs FIXED), `votesFor` (this epoch's tally), `gaugeWeightBps` (the weight the tally
/// resolves to, exactly as `pushWeights` would write it), and `userVotes` (the holder's current
/// allocation) - in one staged multicall. Returns the farm rows plus the epoch, its end, and the
/// holder's votable power.
export function useGaugeVote(user) {
  const gc = ADDRESSES.GAUGE_CONTROLLER
  const em = ADDRESSES.HORIZON_EMISSIONS
  const ve = ADDRESSES.HORIZON_VOTE_ESCROW
  const enabled = !notDeployed(gc) && !notDeployed(em)

  const { data: base, refetch: refetchBase } = useReadContracts({
    query: { enabled, refetchInterval: POLL_SLOW },
    contracts: [
      { address: gc, abi: gaugeAbi, functionName: 'currentEpoch' },
      { address: em, abi: farmAbi, functionName: 'vaultFarmCount' },
      { address: ve, abi: veAbi, functionName: 'votingPowerOf', args: [user ?? ZERO_ADDR] },
    ],
  })
  const epoch = base?.[0]?.result
  const count = Number(base?.[1]?.result ?? 0n)
  const userPower = base?.[2]?.result ?? 0n

  const { data: reads, refetch: refetchFarms } = useReadContracts({
    query: { enabled: enabled && count > 0 && epoch != null, refetchInterval: POLL_SLOW },
    contracts: Array.from({ length: count }, (_, i) => [
      { address: em, abi: farmAbi, functionName: 'getVaultFarm', args: [BigInt(i)] },
      { address: gc, abi: gaugeAbi, functionName: 'farmMode', args: [BigInt(i)] },
      { address: gc, abi: gaugeAbi, functionName: 'votesFor', args: [BigInt(i)] },
      { address: gc, abi: gaugeAbi, functionName: 'gaugeWeightBps', args: [BigInt(i)] },
      { address: gc, abi: gaugeAbi, functionName: 'userVotes', args: [epoch ?? 0n, user ?? ZERO_ADDR, BigInt(i)] },
    ]).flat(),
  })

  const farms = useMemo(() => Array.from({ length: count }, (_, i) => {
    const vf = reads?.[i * 5]?.result
    const mode = reads?.[i * 5 + 1]?.result
    const votes = reads?.[i * 5 + 2]?.result ?? 0n
    const weightBps = reads?.[i * 5 + 3]?.result ?? 0n
    const userVote = reads?.[i * 5 + 4]?.result ?? 0n
    return {
      id: i,
      vault: vf?.vault ?? ZERO_ADDR,
      kind: Number(vf?.kind ?? 0),
      votable: !!mode?.[0],
      fixedBps: Number(mode?.[1] ?? 0),
      votes,
      weightBps: Number(weightBps),
      userVote,
    }
  }), [reads, count])

  const epochEndsAt = epoch != null ? (Number(epoch) + 1) * WEEK : 0

  const refetch = async () => {
    await Promise.all([refetchBase(), refetchFarms()].map((p) => p?.catch?.(() => {}) ?? p))
  }

  return { farms, epoch, epochEndsAt, userPower, loaded: !!base, notDeployed: !enabled, refetch }
}
